import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

interface BackButtonProps {
  /** Rota de destino. Use "-1" para voltar ao histórico anterior. */
  to?: string;
  label?: string;
  className?: string;
  onClick?: () => void;
  /** "solid" = pílula branca (padrão). "subtle" = ghost discreto, sem fundo. */
  variant?: "solid" | "subtle";
}

/**
 * Botão "Voltar" padrão do app.
 * - solid (default): pílula branca com seta rosa, destaque em páginas focadas.
 * - subtle: versão delicada (icon + label fininho) para páginas mais arejadas.
 */
const BackButton: React.FC<BackButtonProps> = ({
  to = "/perfil",
  label = "Voltar",
  className = "",
  onClick,
  variant = "solid",
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) return onClick();
    if (to === "-1") return navigate(-1);
    navigate(to);
  };

  if (variant === "subtle") {
    return (
      <button
        onClick={handleClick}
        aria-label={label}
        className={`inline-flex items-center gap-1.5 -ml-1 pl-1 pr-2 py-1 rounded-full text-[12.5px] font-medium text-text-muted hover:text-brand transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 ${className}`}
      >
        <ArrowLeft className="w-3.5 h-3.5 text-brand/70" strokeWidth={2} />
        {label}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      aria-label={label}
      className={`inline-flex items-center gap-2 pl-2 pr-3.5 py-2 rounded-full bg-card border border-border-soft/70 text-[13px] font-semibold text-text-strong hover:border-brand/40 hover:text-brand transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${className}`}
      style={{
        boxShadow: "0 2px 10px -2px rgba(253,37,151,0.10), 0 1px 2px rgba(0,0,0,0.03)",
      }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, hsl(var(--brand) / 0.14), hsl(var(--brand) / 0.06))",
          color: "hsl(var(--brand))",
        }}
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
      </span>
      {label}
    </button>
  );
};

export default BackButton;
